/**
 * Booking-link data for a booking type (short code + public slug + share base
 * URL), fetched through React Query so the value is **cached across component
 * remounts**.
 *
 * Why this exists: the booking-link section lives in the CRM slideout, which
 * fully unmounts when another slideout is pushed on top and remounts on
 * back-navigation. The section previously fetched with a raw `apiGet` into
 * local `useState`, so that state was lost on every remount — the section
 * rendered empty on back-navigation and flashed in late on every open. React
 * Query caches the response by key, so a remount within `staleTime` repaints
 * instantly from cache instead of paying a fresh network round-trip.
 *
 * This data is NOT a CRM entity property (it lives in the `BookingTypeShortCode`
 * table with a global-uniqueness constraint), so it does not ride the CRM
 * object store / WebSocket path — see `docs/claude/CRM_STORE.md` § Anti-pattern
 * for the rationale. A cached React Query hook is the correct Pattern B home for
 * it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiPatch } from '@/lib/http/client';

export interface BookingLinkData {
  short_code: string;
  global_slug?: string | null;
  /**
   * Configured booking-link base URL (e.g. `https://meet.ahoy.ai`) sourced from
   * RuntimeConfig per-deployable. Optional for backward compatibility with
   * older API responses; missing values fall back to `window.location.origin`
   * at the call site.
   */
  share_base_url?: string;
  /** Username of the booking type's owner (always present on 200 responses). */
  owner_username: string;
  /** Short-code slug for this booking type (always present on 200 responses). */
  slug: string;
}

/** React Query key for a booking type's booking-link data. */
export function bookingLinkQueryKey(
  orgId: number,
  entityId: string
): readonly [string, number, string] {
  return ['scheduling-booking-link', orgId, entityId] as const;
}

interface UseBookingLinkResult {
  /** Booking-link data, or undefined while loading / on error (incl. 404). */
  data: BookingLinkData | undefined;
  isLoading: boolean;
}

/**
 * Fetch a booking type's booking-link data.
 *
 * A 404 is expected for legacy booking types created before the short-code
 * post-commit callback existed. Retries are disabled so a legacy 404 (or any
 * error) resolves immediately to "no data" — the section renders nothing rather
 * than retry-storming a permanently-missing row.
 */
export function useBookingLink(
  orgId: number | undefined,
  entityId: string | undefined
): UseBookingLinkResult {
  // **[Checkout]** Fixed CRITICAL: Prevented unsafe fallback values in query key.
  // Previously used `orgId ?? 0` and `entityId ?? ''`, which caused all
  // undefined-orgId requests to share cache key [..., 0, ...] and all
  // undefined-entityId to share [..., '', ...], risking data contamination if
  // the enabled guard failed. Now query key uses actual values only if enabled.
  const queryKey =
    orgId != null && entityId != null
      ? bookingLinkQueryKey(orgId, entityId)
      : null;

  const { data, isLoading } = useQuery({
    queryKey: queryKey ?? ['scheduling-booking-link-disabled'],
    queryFn: () =>
      apiGet<BookingLinkData>(
        `/api/orgs/${orgId}/scheduling/booking-types/${entityId}/booking-link/`
      ),
    enabled: Boolean(queryKey),
    // Booking-link data changes only when this user edits the slug (which
    // updates the cache directly via useUpdateBookingLinkSlug). Cache it for a
    // few minutes so back-navigation to the slideout repaints instantly.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return { data, isLoading };
}

/**
 * Update a booking type's public URL slug (`global_slug`).
 *
 * On success, the booking-link query cache is updated in place so the section
 * reflects the new slug immediately without a refetch. Callers handle the 409
 * (slug taken) rejection for user feedback.
 */
export function useUpdateBookingLinkSlug(orgId: number, entityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (globalSlug: string) =>
      apiPatch(`/api/orgs/${orgId}/scheduling/booking-types/${entityId}/`, {
        global_slug: globalSlug,
      }),
    onSuccess: (_data, globalSlug) => {
      // The PATCH endpoint returns CrmObjectSerializer (a CRM entity), not
      // BookingLinkData — it has no global_slug, short_code, or share_base_url.
      // We use the mutation variable (globalSlug) as the authoritative new value
      // so the section reflects the edit immediately without a refetch.
      queryClient.setQueryData<BookingLinkData>(
        bookingLinkQueryKey(orgId, entityId),
        (prev: BookingLinkData | undefined) => {
          if (!prev) {
            return prev;
          }
          return { ...prev, global_slug: globalSlug };
        }
      );
    },
  });
}
